import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';

const AnimatedButton = ({ to, children, className = "" }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    // Generate random particles for shimmer effect
    const generateParticles = () => {
      const newParticles = [];
      for (let i = 0; i < 8; i++) {
        newParticles.push({
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          delay: Math.random() * 2,
          duration: 2 + Math.random() * 1
        });
      }
      setParticles(newParticles);
    };

    generateParticles();
    const interval = setInterval(generateParticles, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Link
      to={to}
      className={`
        relative overflow-hidden group
        inline-flex items-center justify-center
        px-8 py-4 rounded-full font-bold text-base
        bg-gradient-to-r from-[#5a4bff] via-[#7c3aed] to-[#b74bff]
        text-white shadow-2xl
        transform transition-all duration-300 ease-out
        hover:scale-110 hover:shadow-[0_20px_40px_rgba(109,40,217,0.5)]
        focus:ring-4 focus:ring-[#b74bff] focus:outline-none
        animate-pulse-slow
        ${className}
      `}
      style={{
        background: isHovered 
          ? 'linear-gradient(135deg, #6366f1, #8b5cf6, #d946ef, #f59e0b)'
          : 'linear-gradient(135deg, #5a4bff, #7c3aed, #b74bff)',
        backgroundSize: '200% 200%',
        animation: 'gradient-shift 3s ease infinite, float 4s ease-in-out infinite'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background glow effect */}
      <div 
        className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: 'radial-gradient(circle, rgba(109,40,217,0.4) 0%, transparent 70%)',
          transform: 'scale(1.5)',
          filter: 'blur(10px)'
        }}
      />
      
      {/* Shimmer particles */}
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute w-1 h-1 bg-white rounded-full opacity-0 group-hover:opacity-100"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            animation: `sparkle ${particle.duration}s ease-in-out ${particle.delay}s infinite`
          }}
        />
      ))}
      
      {/* Button content */}
      <span className="relative z-10 flex items-center gap-2">
        {children}
        <span className="inline-block transform transition-transform group-hover:translate-x-1">
          ✨
        </span>
      </span>
      
      {/* Ripple effect overlay */}
      <div 
        className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-20 transition-opacity duration-300"
        style={{
          background: 'radial-gradient(circle, white 10%, transparent 10%)',
          backgroundSize: '15% 15%',
          animation: isHovered ? 'ripple 0.6s linear' : 'none'
        }}
      />

      <style jsx>{`
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        
        @keyframes sparkle {
          0%, 100% { opacity: 0; transform: scale(0); }
          50% { opacity: 1; transform: scale(1); }
        }
        
        @keyframes ripple {
          0% { transform: scale(0); opacity: 0.3; }
          100% { transform: scale(2); opacity: 0; }
        }
        
        .animate-pulse-slow {
          animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </Link>
  );
};

export default AnimatedButton;
